import { ApiService } from '@tager/web-core';

import { getWebsiteSource } from '@/utils/common';
import { getUtmFromCookie } from '@/utils/cookies';
import {
  getCaptchaRequestParams,
  mergeCaptchaFetchOptions,
  WithCaptchaToken,
} from '@/utils/captcha';

import {
  AmpLandingConsultationParams,
  AutoSelectionLandingParams,
  B2bCreditFormParams,
  B2bServiceFormParams,
  CabinetCarValuationParams,
  CabinetRequestPartsParams,
  CarAdvParams,
  CarCaraccidenthelpParams,
  CarCatalogParams,
  CarCheckRequestParams,
  CarHelperParams,
  CarModelComplectationsParams,
  CarModelParams,
  CarModelTestDriveParams,
  CarPurchaseCallbackParams,
  CarSubscriptionParams,
  ContactsRequestParams,
  CreditAlfabankProductParams,
  CreditLeasingRequestParams,
  CreditProductParams,
  DetailedCarConsultationParams,
  DetailedCarOrderValues,
  DetailedCarTestDriveOfflineParams,
  DetailedCarTestDriveParams,
  ElectrocarsLandingChargersParams,
  ElectrocarsLandingConsultationParams,
  ElectrocarsLandingSelectionCarParams,
  ElectrocarsLandingTestDriveParams,
  FinanceLandingCalculationParams,
  FinanceLandingProgramParams,
  FormCallbackParams,
  HelpOnRoadCallRequestParams,
  HisunCarsLeadFormParams,
  HisunCreditFormParams,
  HisunServiceFormParams,
  JacCarsLeadFormParams,
  JacCreditFormParams,
  JacServiceFormParams,
  JacTestDriveFormParams,
  JetourCarsLeadFormParams,
  PartnersCarsRequestParams,
  PartnersCarsTestDriveParams,
  RateCarOfflineParams,
  RefitConsultationBody,
  ServiceBenefitsParams,
  ServiceConsultationParams,
  ServiceKoParams,
  ServiceRequestBodyRepairParams,
  ServiceRequestParams,
  ServiceValuationLandingConsultationParams,
  ServiceValuationLandingRequestParams,
  SollersCarsLeadFormParams,
  SollersCreditFormParams,
  SpecialOfferParams,
  TheLineupCarParams,
  TheLineupTestDriveParams,
  TopLevelFunnelLandingConsultationParams,
  TopLevelFunnelLandingCreditParams,
  TopLevelFunnelLandingSelectionCarParams,
  VedaemLandingParams,
} from './typings';

const baseRequest = new ApiService({
  baseUrl: {
    csr: process.env.NEXT_PUBLIC_LEADS_SERVICE_URL,
    ssr: process.env.NEXT_PUBLIC_LEADS_SERVICE_URL,
  },
}).getRequest();

const request = {
  ...baseRequest,
  post: (options: Parameters<typeof baseRequest.post>[0]) => {
    const body = options.body;

    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      'captchaToken' in body
    ) {
      const { captchaToken, ...rest } = body as Record<string, unknown> & {
        captchaToken?: string;
      };

      if (!captchaToken) {
        return baseRequest.post({ ...options, body: rest });
      }

      return baseRequest.post({
        ...options,
        body: rest,
        fetchOptions: mergeCaptchaFetchOptions(
          captchaToken,
          options.fetchOptions
        ),
      });
    }

    return baseRequest.post(options);
  },
};

function postLead<T extends object>(path: string, body: WithCaptchaToken<T>) {
  const { body: requestBody, fetchOptions } = getCaptchaRequestParams(body);

  return request.post({
    path,
    body: requestBody,
    fetchOptions,
  });
}

/** Hisun **/
export function submitHisunCarsLeadForm(
  params: WithCaptchaToken<HisunCarsLeadFormParams>
) {
  return postLead('/atlantm/hisun/cars-lead', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitHisunCreditForm(
  params: WithCaptchaToken<HisunCreditFormParams>
) {
  return postLead('/atlantm/hisun/credit', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitHisunServiceForm(
  params: WithCaptchaToken<HisunServiceFormParams>
) {
  return postLead('/atlantm/hisun/service', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** JAC **/
export function submitJacCarsLeadForm(
  params: WithCaptchaToken<JacCarsLeadFormParams>
) {
  return postLead('/atlantm/jac/cars-lead', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitJacCreditForm(
  params: WithCaptchaToken<JacCreditFormParams>
) {
  return postLead('/atlantm/jac/credit', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitJacServiceForm(
  params: WithCaptchaToken<JacServiceFormParams>
) {
  return postLead('/atlantm/jac/service', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitJacTestDriveForm(
  params: WithCaptchaToken<JacTestDriveFormParams>
) {
  return postLead('/atlantm/jac/test-drive', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Jetour **/
export function submitJetourCarsLeadForm(
  params: WithCaptchaToken<JetourCarsLeadFormParams>
) {
  return postLead('/atlantm/jetour/cars-lead', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Sollers **/
export function submitSollersCarsLeadForm(
  params: WithCaptchaToken<SollersCarsLeadFormParams>
) {
  return postLead('/atlantm/sollers/car', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitSollersCreditForm(
  params: WithCaptchaToken<SollersCreditFormParams>
) {
  return postLead('/atlantm/sollers/credit', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** B2b **/
export function submitB2bCreditForm(
  params: WithCaptchaToken<B2bCreditFormParams>
) {
  return postLead('/atlantm/b2b/credit', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitB2bServiceForm(
  params: WithCaptchaToken<B2bServiceFormParams>
) {
  return postLead('/atlantm/b2b/service', {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car check **/
export function submitCarCheck(
  params: WithCaptchaToken<CarCheckRequestParams>
): Promise<boolean> {
  return postLead(`/atlantm/car-check`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  }) as Promise<boolean>;
}

/** Help on road **/
export function helpOnRoadCall(
  params: WithCaptchaToken<HelpOnRoadCallRequestParams>
): Promise<boolean> {
  return postLead(`/atlantm/help-on-road/call`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  }) as Promise<boolean>;
}

/** Detailed car **/
export function submitDetailedCarOrder(params: DetailedCarOrderValues) {
  return postLead(`/atlantm/cars/request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitDetailedCarTestDrive(
  params: WithCaptchaToken<DetailedCarTestDriveParams>
) {
  return postLead(`/atlantm/cars/test-drive`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitDetailedCarConsultation(
  params: WithCaptchaToken<DetailedCarConsultationParams>
) {
  return postLead(`/atlantm/cars/consultation`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitPartnersCarsRequest(
  params: WithCaptchaToken<PartnersCarsRequestParams>
) {
  return postLead(`/atlantm/cars/partner-cars-request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitPartnersCarsTestDrive(
  params: WithCaptchaToken<PartnersCarsTestDriveParams>
) {
  return postLead(`/atlantm/cars/partner-cars-test-drive`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitDetailedCarTestDriveOffline(
  params: WithCaptchaToken<DetailedCarTestDriveOfflineParams>
) {
  return postLead(`/atlantm/cars/test-drive-request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitCreditProduct(
  params: WithCaptchaToken<CreditProductParams>
) {
  return postLead(`/atlantm/cars/credit-product-request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitCreditAlfabankProduct(
  params: WithCaptchaToken<CreditAlfabankProductParams>
) {
  return postLead(`/atlantm/cars/credit-product-alfabank-request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** AMP Landing **/
export function submitAmpLandingConsultation(
  params: WithCaptchaToken<AmpLandingConsultationParams>
) {
  return postLead(`/atlantm/amp-landing`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Call request **/
export function submitFormCallback(
  params: WithCaptchaToken<FormCallbackParams>
) {
  return postLead(`/atlantm/request-call`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car subscription **/
export function submitCarSubscription(
  params: WithCaptchaToken<CarSubscriptionParams>
) {
  return postLead(`/atlantm/car-subscription`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car adv **/
export function submitCarAdv(params: WithCaptchaToken<CarAdvParams>) {
  return postLead(`/atlantm/car-adv`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car caraccidenthelp **/
export function submitCarCaraccidenthelp(
  params: WithCaptchaToken<CarCaraccidenthelpParams>
) {
  return postLead(`/atlantm/car-caraccidenthelp`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car purchase **/
export function submitCarPurchaseCallback(
  params: WithCaptchaToken<CarPurchaseCallbackParams>
) {
  return postLead(`/atlantm/car-purchase`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Service **/
export function submitServiceRequest(
  params: WithCaptchaToken<ServiceRequestParams>
) {
  return postLead(`/atlantm/service/request`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitServiceRequestBodyRepair(
  params: WithCaptchaToken<ServiceRequestBodyRepairParams>
) {
  return postLead(`/atlantm/service/request-body-repair`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitServiceConsultation(
  params: WithCaptchaToken<ServiceConsultationParams>
) {
  return postLead(`/atlantm/service/consultation`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Car model **/
export function submitCarModel(params: WithCaptchaToken<CarModelParams>) {
  return postLead(`/atlantm/car-model`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitCarModelTestDrive(
  params: WithCaptchaToken<CarModelTestDriveParams>
) {
  return postLead(`/atlantm/car-model/test-drive`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

export function submitCarModelComplectations(
  params: CarModelComplectationsParams
) {
  return request.post({
    path: `/atlantm/car-model/complectations`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Electrocars landing **/
export function submitElectrocarsLandingSelectionCar(
  params: ElectrocarsLandingSelectionCarParams
) {
  return request.post({
    path: `/atlantm/electrocars-landing/selection-car`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitElectrocarsLandingTestDrive(
  params: ElectrocarsLandingTestDriveParams
) {
  return request.post({
    path: `/atlantm/electrocars-landing/test-drive`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitElectrocarsLandingChargers(
  params: ElectrocarsLandingChargersParams
) {
  return request.post({
    path: `/atlantm/electrocars-landing/chargers`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitElectrocarsLandingConsultation(
  params: ElectrocarsLandingConsultationParams
) {
  return request.post({
    path: `/atlantm/electrocars-landing/consultation`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Top level funnel landing **/
export function submitTopLevelFunnelLandingConsultation(
  params: TopLevelFunnelLandingConsultationParams
) {
  return request.post({
    path: `/atlantm/top-level-funnel-landing/consultation`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitTopLevelFunnelLandingCredit(
  params: TopLevelFunnelLandingCreditParams
) {
  return request.post({
    path: `/atlantm/top-level-funnel-landing/credit`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitTopLevelFunnelLandingSelectionCar(
  params: TopLevelFunnelLandingSelectionCarParams
) {
  return request.post({
    path: `/atlantm/top-level-funnel-landing/selection-car`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Cabinet **/
export function submitCabinetRequestParts(params: CabinetRequestPartsParams) {
  return request.post({
    path: `/atlantm/cabinet/request-parts`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitCabinetCarValuation(params: CabinetCarValuationParams) {
  return request.post({
    path: `/atlantm/cabinet/car-valuation`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Special offer **/
export function submitSpecialOffer(params: SpecialOfferParams) {
  return request.post({
    path: `/atlantm/special-offer`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Car helper **/
export function submitCarHelper(params: CarHelperParams) {
  return request.post({
    path: `/atlantm/car-helper`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Service KO **/
export function submitServiceKo(params: ServiceKoParams) {
  return request.post({
    path: `/atlantm/service-ko`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Car catalog **/
export function submitCarCatalog(params: CarCatalogParams) {
  return request.post({
    path: `/atlantm/car-catalog`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Rate car **/
export function submitRateCarOffline(params: RateCarOfflineParams) {
  return postLead(`/atlantm/rate-car/offline`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Contacts **/
export function submitContactsRequest(
  params: WithCaptchaToken<ContactsRequestParams>
) {
  return postLead(`/atlantm/contacts`, {
    ...params,
    source: getWebsiteSource(),
    utm: getUtmFromCookie(),
  });
}

/** Credit Leasing **/
export function submitCreditLeasingRequest(params: CreditLeasingRequestParams) {
  return request.post({
    path: `/atlantm/credit-leasing`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** The Lineup **/
export function submitTheLineupCar(params: TheLineupCarParams) {
  return request.post({
    path: `/atlantm/the-lineup/car`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitTheLineupTestDrive(params: TheLineupTestDriveParams) {
  return request.post({
    path: `/atlantm/the-lineup/test-drive`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Vedaem Landing **/
export function submitVedaemLanding(params: VedaemLandingParams) {
  return request.post({
    path: `/atlantm/vedaem-landing`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Auto Selection Landing **/
export function submitAutoSelectionLanding(params: AutoSelectionLandingParams) {
  return request.post({
    path: `/atlantm/auto-selection-landing`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Service Benefits **/
export function submitServiceBenefits(params: ServiceBenefitsParams) {
  return request.post({
    path: `/atlantm/service-benefits`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Service Valuation Landing **/
export function submitServiceValuationLandingRequest(
  params: ServiceValuationLandingRequestParams
) {
  return request.post({
    path: `/atlantm/service-valuation-landing/request`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitServiceValuationLandingConsultation(
  params: ServiceValuationLandingConsultationParams
) {
  return request.post({
    path: `/atlantm/service-valuation-landing/consultation`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Finance Landing **/
export function submitFinanceLandingCalculation(
  params: FinanceLandingCalculationParams
) {
  return request.post({
    path: `/atlantm/finance-landing/calculation`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

export function submitFinanceLandingProgram(
  params: FinanceLandingProgramParams
) {
  return request.post({
    path: `/atlantm/finance-landing/program`,
    body: { ...params, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}

/** Refit Landing **/
export function submitRefitConsultation(body: RefitConsultationBody) {
  return request.post({
    path: `/atlantm/service/request-car-conversion`,
    body: { ...body, source: getWebsiteSource(), utm: getUtmFromCookie() },
  });
}
